import { Link } from "react-router-dom";
import { ShieldCheck, Rocket, Scale, ArrowUpRight, ArrowRight } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { Reveal } from "@/components/Reveal";

const pillars = [
  {
    icon: ShieldCheck,
    name: "Protect",
    need: "Reduce cybersecurity risk",
    desc: "Find and close the gaps attackers actually use — then keep them closed with continuous monitoring and response.",
    links: [
      { label: "Baseline Review ($499)", to: "/baseline-review" },
      { label: "Security Assessment", to: "/services/security-assessment" },
      { label: "Penetration Testing", to: "/services/penetration-testing" },
      { label: "Application Security", to: "/services/application-security" },
      { label: "Managed Security", to: "/services/managed-security" },
    ],
  },
  {
    icon: Rocket,
    name: "Transform",
    need: "Modernize safely",
    desc: "Adopt cloud and AI with security built in from day one — architecture, identity, and automation done right.",
    links: [
      { label: "Cloud Security", to: "/services/cloud-security" },
      { label: "AI Security", to: "/services/ai-security" },
      { label: "Technology Strategy & vCISO", to: "/services/vciso" },
      { label: "BArecord — AI communications archive", to: "/barecord" },
      { label: "SecureDesk — AI support & security ops", to: "/products/securedesk" },
    ],
  },
  {
    icon: Scale,
    name: "Govern",
    need: "Prove control and compliance",
    desc: "Turn security into evidence auditors, insurers, and enterprise customers accept — frameworks, policies, and proof.",
    links: [
      { label: "Compliance Readiness (SOC 2 / ISO / HIPAA)", to: "/services/compliance" },
      { label: "Threat Modeling", to: "/services/threat-modeling" },
      { label: "Cybersecurity Program", to: "/services/cybersecurity" },
      { label: "Trust Center", to: "/trust" },
      { label: "Our SOC 2 Journey", to: "/trust/soc2-journey" },
    ],
  },
];

export default function Approach() {
  return (
    <PageLayout
      seoTitle="Our Approach — Protect, Transform, Govern | Aliwer"
      seoDescription="How Aliwer organizes security work: Protect (reduce cyber risk), Transform (modernize with cloud and AI safely), and Govern (prove control and compliance)."
      path="/approach"
      eyebrow="Our approach"
      title="Protect. Transform. Govern."
      subtitle="Every Aliwer engagement serves one of three customer needs. Start where it hurts most — each pillar connects to the others through the same method: Assess → Design → Protect → Monitor → Prove."
      testid="approach-page"
    >
      <div className="space-y-5 mb-16" data-testid="approach-pillars">
        {pillars.map((p, i) => (
          <Reveal key={p.name} delay={i * 0.05}>
            <div className="grid md:grid-cols-[1fr_1.2fr] gap-8 rounded-2xl border border-white/10 bg-surface p-8 md:p-10" data-testid={`approach-pillar-${p.name.toLowerCase()}`}>
              <div>
                <p.icon size={26} className="text-paper" strokeWidth={1.2} />
                <h2 className="mt-5 font-display text-3xl sm:text-4xl tracking-tight text-paper">{p.name}</h2>
                <p className="mt-1 text-xs uppercase tracking-widest text-fog">{p.need}</p>
                <p className="mt-4 text-sm text-fog leading-relaxed max-w-md">{p.desc}</p>
              </div>
              <div className="rounded-2xl border border-white/10 overflow-hidden divide-y divide-white/10 self-start">
                {p.links.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`approach-link-${l.to.replace(/\//g, "-").slice(1)}`}
                    className="group flex items-center justify-between px-5 py-3.5 bg-black/40 hover:bg-black/70 transition-colors"
                  >
                    <span className="text-sm text-paper">{l.label}</span>
                    <ArrowUpRight size={14} className="text-fog transition-all duration-300 group-hover:text-paper group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <div className="rounded-2xl border border-white/15 bg-surface p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-8" data-testid="approach-cta">
          <div className="flex-1">
            <h2 className="font-display text-2xl tracking-tight text-paper">Not sure which pillar you need?</h2>
            <p className="mt-3 text-sm text-fog leading-relaxed max-w-2xl">
              Start with a free 20-minute consult — we'll tell you honestly where you stand and which path
              delivers the most protection for the least money. Or get a concrete answer fast with the{" "}
              <Link to="/baseline-review" className="underline underline-offset-2 text-paper hover:text-fog transition-colors">
                $499 Baseline Review
              </Link>.
            </p>
          </div>
          <Link
            to="/register"
            data-testid="approach-consult-btn"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-paper text-black text-sm font-medium px-8 py-4 hover:bg-white transition-colors shrink-0"
          >
            Book a free consult
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        </div>
      </Reveal>
    </PageLayout>
  );
}
